import _isArray from 'lodash/isArray';
import _isObject from 'lodash/isObject';
import _isString from 'lodash/isString';
import _remove from 'lodash/remove';
import _values from 'lodash/values';
import _pick from 'lodash/pick';
import { RunnitsOwnerSelection } from '@/store';
import { CLUB_OFFER_PLAN_INFO, ClubKey, RunnitsAccessLevel } from '@run-diffusion/shared';
import { BoardViewMode, BoardSortOption } from '@/views/Runnits/pages/OLD_DESIGN_RunnitBoardsPage/types';
import { BoardsFilter } from '@/views/Runnits/pages/RunnitBoardsPage/types';

export type PlatformHomePreference = 'RD' | 'RUNNIT';

const CIV_MODEL_ID: string = 'CIV_MODEL_ID';
const CIV_MODEL_VERSION_ID: string = 'CIV_MODEL_VERSION_ID';
const SESSION_SOUNDS_OFF: string = 'SESSION_SOUNDS_OFF';
const TEAM_ID: string = 'TEAM_ID';
const WORKSPACE_ID: string = 'WORKSPACE_ID';
const ANNOUNCEMENT_DISMISSED: string = 'ANNOUNCEMENT_DISMISSED';
const COMFY_ONBOARDING_DISMISSED: string = 'COMFY_ONBOARDING_DISMISSED';
const PERSIST_EXTENSIONS: string = 'PERSIST_EXTENSIONS';
const PROXY_ADMIN_TOKEN: string = 'PROXY_ADMIN_TOKEN';
const PROXY_ADMIN_ID_TOKEN: string = 'PROXY_ADMIN_ID_TOKEN';
const LAST_SSO_PROVIDER_VAL: string = 'LAST_SSO_PROVIDER_VAL';
// This browser's Connected Device id, minted when it registers as the
// first-party web client. Per browser by design: clearing site data makes this
// a new client, which is the honest outcome, and the old record stays revocable
// from Manage Devices.
const WEB_CLIENT_DEVICE_ID: string = 'WEB_CLIENT_DEVICE_ID';
// Enforced-SSO team-invite auto-login (session-scoped: must survive the signInWithRedirect
// round-trip in the same tab, but must NOT leak across fresh visits to a new invite link).
const PENDING_SSO_TEAM_INVITE: string = 'PENDING_SSO_TEAM_INVITE';
const SSO_INVITE_REDIRECT_ATTEMPTED: string = 'SSO_INVITE_REDIRECT_ATTEMPTED';
const SSO_INVITE_EMAIL_MISMATCH: string = 'SSO_INVITE_EMAIL_MISMATCH';
const PLATFORM_HOME_PREFERENCE: string = 'PLATFORM_HOME_PREFERENCE';
const CHECKOUT_PROMOTION_CODES: string = 'CHECKOUT_PROMOTION_CODES';
const ACCEPTED_REFERRAL_CODE: string = 'ACCEPTED_REFERRAL_CODE';
const ACCEPTED_REFERRAL_DISMISSED: string = 'ACCEPTED_REFERRAL_DISMISSED';
// Human Check Pass minted on the login page, read back by captureSignInData after sign-in. localStorage (not
// session) because the signInWithRedirect flows reload the page before the first sign-in capture runs.
const HUMAN_CHECK_PASS: string = 'HUMAN_CHECK_PASS';
const MOBIUS_ENABLED: string = 'MOBIUS_ENABLED';
// Fingerprint of the last [userId, personProps] pair handed to
// analytics.identify(). Persisted per browser so an ordinary page load does not
// re-emit an identical $set: identify was firing about once per load, which is
// most of the $set volume. account_age_days is part of the fingerprint, so this
// still re-identifies once a day per device and person properties never go
// stale by more than that. See ADR-0036.
const ANALYTICS_IDENTITY_FINGERPRINT: string = 'ANALYTICS_IDENTITY_FINGERPRINT';

// RUNNITS - LOCAL STORAGE
const RUNNITS_HOME_LIBRARY_SELECTION: string = 'RUNNITS_HOME_LIBRARY_SELECTION';
const RUNNITS_HOME_BOARDS_SELECTION: string = 'RUNNITS_HOME_BOARDS_SELECTION';
const RUNNITS_TOOLS_DIALOG_NAV_TAB: string = 'RUNNITS_TOOLS_DIALOG_NAV_TAB';
const RUNNITS_SELECTED_IMAGE_MODE: string = 'RUNNITS_SELECTED_IMAGE_MODE';
const RUNNITS_SELECTED_COLUMNS: string = 'RUNNITS_SELECTED_COLUMNS';
const RUNNITS_SELECTED_PROMPT_VIEW: string = 'RUNNITS_SELECTED_PROMPT_VIEW';
const RUNNITS_OWNER_SELECTION: string = 'RUNNITS_OWNER_SELECTION';
const RUNNITS_TEAM_ACCESS_LEVEL: string = 'RUNNITS_TEAM_ACCESS_LEVEL';
const RUNNITS_INPAINTING_IS_EXPANDED: string = 'RUNNITS_INPAINTING_IS_EXPANDED';

// RUNNITS BOARD MANAGEMENT - LOCAL STORAGE
const RUNNITS_BOARD_VIEW_MODE: string = 'RUNNITS_BOARD_VIEW_MODE';
const RUNNITS_BOARD_SEARCH_QUERY: string = 'RUNNITS_BOARD_SEARCH_QUERY';
const RUNNITS_BOARD_SORT_OPTION: string = 'RUNNITS_BOARD_SORT_OPTION';
const RUNNITS_BOARD_PROJECT_FILTER: string = 'RUNNITS_BOARD_PROJECT_FILTER';

// TEAM/COMPANY MANAGEMENT - LOCAL STORAGE

const RUNNIT_BOARD_TEMPLATES_FILTER: string = 'RUNNIT_BOARD_TEMPLATES_FILTER';
const RUNNITS_TRAINING_ONBOARDING_DISMISSED: string = 'RUNNITS_TRAINING_ONBOARDING_DISMISSED';

// RUNNITS SIDE NAV EXPANSION STATE
const RUNNITS_SIDE_NAV_GENERATE_EXPANDED: string = 'RUNNITS_SIDE_NAV_GENERATE_EXPANDED';
const RUNNITS_SIDE_NAV_RESOURCES_EXPANDED: string = 'RUNNITS_SIDE_NAV_RESOURCES_EXPANDED';

// RUNNITS LIBRARY FILTER LOCATION
type RunnitLibraryFilterLocation = 'RUNNITS_LIBRARY' | 'RUNNITS_SINGLE_TOOL' | 'RUNNITS_HOME';
export const RUNNIT_LIBRARY_FILTER_LOCATION: Record<RunnitLibraryFilterLocation, RunnitLibraryFilterLocation> = {
	'RUNNITS_LIBRARY': 'RUNNITS_LIBRARY',
	'RUNNITS_SINGLE_TOOL': 'RUNNITS_SINGLE_TOOL',
	'RUNNITS_HOME': 'RUNNITS_HOME',
};

// Helper values
const trueVal: string = '1';

// Helper functions
const lsGet = (key: string) => {
	if (window && window.localStorage && window.localStorage.getItem) {
		try {
			return window.localStorage.getItem(key);
		} catch (e) {
			// If the browser is incognito, this will error
			console.error(e);
			return null;
		}
	}
	return null;
};
const lsSet = (key: string, value: string) => {
	if (window && window.localStorage && window.localStorage.setItem) {
		try {
			window.localStorage.setItem(key, value);
		} catch (e) {
			// If the browser is incognito, this will error
			console.error(e);
		}
	}
};
const lsRemove = (key: string) => {
	if (window && window.localStorage && window.localStorage.removeItem) {
		try {
			window.localStorage.removeItem(key);
		} catch (e) {
			// If the browser is incognito, this will error
			console.error(e);
		}
	}
};

// sessionStorage variants — scoped to the tab/session, cleared when it ends.
const ssGet = (key: string): string => {
	if (window && window.sessionStorage && window.sessionStorage.getItem) {
		try {
			return window.sessionStorage.getItem(key);
		} catch (e) {
			console.error(e);
			return null;
		}
	}
	return null;
};
const ssSet = (key: string, value: string) => {
	if (window && window.sessionStorage && window.sessionStorage.setItem) {
		try {
			window.sessionStorage.setItem(key, value);
		} catch (e) {
			console.error(e);
		}
	}
};
const ssRemove = (key: string) => {
	if (window && window.sessionStorage && window.sessionStorage.removeItem) {
		try {
			window.sessionStorage.removeItem(key);
		} catch (e) {
			console.error(e);
		}
	}
};

// CIV_MODEL_ID
export const setCivitaiModelId = (modelId: string): void => {
	lsSet(CIV_MODEL_ID, modelId);
};
export const getCivitaiModelId = (): string => lsGet(CIV_MODEL_ID);
export const removeCivitaiModelId = (): void => {
	lsRemove(CIV_MODEL_ID);
};

// CIV_MODEL_VERSION_ID
export const setCivitaiModelVersionId = (modelVersionId: string): void => {
	lsSet(CIV_MODEL_VERSION_ID, modelVersionId);
};
export const getCivitaiModelVersionId = (): string => lsGet(CIV_MODEL_VERSION_ID);
export const removeCivitaiModelVersionId = (): void => {
	lsRemove(CIV_MODEL_VERSION_ID);
};

// SESSION_SOUNDS_OFF
export const setSessionSoundsOff = (): void => {
	lsSet(SESSION_SOUNDS_OFF, trueVal);
};
export const getSessionSoundsOff = (): boolean => lsGet(SESSION_SOUNDS_OFF) === trueVal;
export const removeSessionSoundsOff = (): void => {
	lsRemove(SESSION_SOUNDS_OFF);
};

// MOBIUS_ENABLED — persist the Mobius/Default toggle so it survives page reloads
export const setMobiusEnabled = (enabled: boolean): void => {
	if (enabled) {
		lsSet(MOBIUS_ENABLED, trueVal);
	} else {
		lsRemove(MOBIUS_ENABLED);
	}
};
export const getMobiusEnabled = (): boolean => lsGet(MOBIUS_ENABLED) === trueVal;

// TEAM_ID
export const setTeamId = (teamId: string): void => {
	lsSet(TEAM_ID, teamId);
};
export const getTeamId = (): string => lsGet(TEAM_ID);
export const removeTeamId = (): void => {
	lsRemove(TEAM_ID);
};

// PENDING_SSO_TEAM_INVITE — the Invite Context (+ resolved SSO provider) for an enforced-SSO
// team invite, persisted so it survives the full-page signInWithRedirect into the SAML provider.
export interface PendingSsoTeamInvite {
	teamId: string
	inviteCode: string
	ssoProvider: string
}
export const setPendingSsoTeamInvite = (invite: PendingSsoTeamInvite): void => {
	ssSet(PENDING_SSO_TEAM_INVITE, JSON.stringify(invite));
};
export const getPendingSsoTeamInvite = (): PendingSsoTeamInvite | null => {
	const raw: string = ssGet(PENDING_SSO_TEAM_INVITE);
	if (!raw) return null;
	try {
		return JSON.parse(raw) as PendingSsoTeamInvite;
	} catch (e) {
		console.error(e);
		return null;
	}
};
export const removePendingSsoTeamInvite = (): void => {
	ssRemove(PENDING_SSO_TEAM_INVITE);
};

// SSO_INVITE_REDIRECT_ATTEMPTED — one-shot guard (value = inviteCode) so a cancelled/failed SSO
// return does not immediately re-fire the redirect and trap the invitee in a bounce-loop.
export const setSsoInviteRedirectAttempted = (inviteCode: string): void => {
	ssSet(SSO_INVITE_REDIRECT_ATTEMPTED, inviteCode);
};
export const getSsoInviteRedirectAttempted = (): string => ssGet(SSO_INVITE_REDIRECT_ATTEMPTED);
export const removeSsoInviteRedirectAttempted = (): void => {
	ssRemove(SSO_INVITE_REDIRECT_ATTEMPTED);
};

// SSO_INVITE_EMAIL_MISMATCH — set (value = inviteCode) when an enforced-SSO invitee authenticated
// with an account whose email != the invited address, so the re-landing page explains the problem
// instead of silently bouncing them back into the same failing SSO sign-in.
export const setSsoInviteEmailMismatch = (inviteCode: string): void => {
	ssSet(SSO_INVITE_EMAIL_MISMATCH, inviteCode);
};
export const getSsoInviteEmailMismatch = (): string => ssGet(SSO_INVITE_EMAIL_MISMATCH);
export const removeSsoInviteEmailMismatch = (): void => {
	ssRemove(SSO_INVITE_EMAIL_MISMATCH);
};

// WORKSPACE_ID
export const setWorkspaceId = (workspaceId: string): void => {
	lsSet(WORKSPACE_ID, workspaceId);
};
export const getWorkspaceId = (): string => lsGet(WORKSPACE_ID);
export const removeWorkspaceId = (): void => {
	lsRemove(WORKSPACE_ID);
};

// ANNOUNCEMENT_DISMISSED
export const setAnnouncementDismissed = (message: string, userId: string): void => {
	if (userId && message) {
		const existingDismissedAnnouncements: string[] = getAnnouncementsDismissed(userId);
		lsSet(
			`${ANNOUNCEMENT_DISMISSED}-${userId}`,
			JSON.stringify(
				[...existingDismissedAnnouncements, message].filter((message: string) => _isString(message)),
			),
		);
	}
};
export const getAnnouncementsDismissed = (userId: string): string[] => {
	if (userId) {
		const stringifiedDismissedAnnouncements: string = lsGet(`${ANNOUNCEMENT_DISMISSED}-${userId}`);

		try {
			const dismissedAnnouncements = JSON.parse(stringifiedDismissedAnnouncements);
			if (_isArray(dismissedAnnouncements)) {
				return dismissedAnnouncements.filter((message: string) => _isString(message));
			}
		} catch (e) {
			// continue on to remove the local storage value and return empty value;
		}
	}

	return [];
};
export const removeAnnouncementDismissed = (message: string, userId: string): void => {
	if (userId && message) {
		const existingDismissedAnnouncements: string[] = getAnnouncementsDismissed(userId);
		lsSet(
			`${ANNOUNCEMENT_DISMISSED}-${userId}`,
			JSON.stringify(
				_remove(existingDismissedAnnouncements, (msg: string) => msg === message),
			),
		);
	}
};
export const removeAnnouncementDismissedAll = (userId: string): void => {
	if (userId) {
		lsRemove(`${ANNOUNCEMENT_DISMISSED}-${userId}`);
	}
};

// COMFY_ONBOARDING_DISMISSED
export const setComfyOnboardingDismissed = (): void => {
	lsSet(COMFY_ONBOARDING_DISMISSED, trueVal);
};
export const getComfyOnboardingDismissed = (): boolean => lsGet(COMFY_ONBOARDING_DISMISSED) === trueVal;
export const removeComfyOnboardingDismissed = (): void => {
	lsRemove(COMFY_ONBOARDING_DISMISSED);
};

// PERSIST_EXTENSIONS
export const setPersistExtensions = (): void => {
	lsSet(PERSIST_EXTENSIONS, trueVal);
};
export const getPersistExtensions = (): boolean => lsGet(PERSIST_EXTENSIONS) === trueVal;
export const removePersistExtensions = (): void => {
	lsRemove(PERSIST_EXTENSIONS);
};

// PROXY_ADMIN_TOKEN
export const setProxyAdminToken = (token: string): void => {
	lsSet(PROXY_ADMIN_TOKEN, token);
};
export const getProxyAdminToken = (): string => lsGet(PROXY_ADMIN_TOKEN);
export const removeProxyAdminToken = (): void => {
	lsRemove(PROXY_ADMIN_TOKEN);
};

// PROXY_ADMIN_ID_TOKEN
export const setProxyAdminIdToken = (token: string): void => {
	lsSet(PROXY_ADMIN_ID_TOKEN, token);
};
export const getProxyAdminIdToken = (): string => lsGet(PROXY_ADMIN_ID_TOKEN);
export const removeProxyAdminIdToken = (): void => {
	lsRemove(PROXY_ADMIN_ID_TOKEN);
};

// LAST_SSO_PROVIDER_VAL
export const setLastSsoProviderVal = (ssoProviderVal: string): void => {
	lsSet(LAST_SSO_PROVIDER_VAL, ssoProviderVal);
};
export const getLastSsoProviderVal = (): string => lsGet(LAST_SSO_PROVIDER_VAL);
export const removeLastSsoProviderVal = (): void => {
	lsRemove(LAST_SSO_PROVIDER_VAL);
};

// WEB_CLIENT_DEVICE_ID
export const setWebClientDeviceId = (deviceId: string): void => {
	lsSet(WEB_CLIENT_DEVICE_ID, deviceId);
};
// Null when this browser has not registered yet, which callers treat as
// "unregistered" rather than an error.
export const getWebClientDeviceId = (): string | null => lsGet(WEB_CLIENT_DEVICE_ID) || null;
export const removeWebClientDeviceId = (): void => {
	lsRemove(WEB_CLIENT_DEVICE_ID);
};

// ANALYTICS_IDENTITY_FINGERPRINT
export const setAnalyticsIdentityFingerprint = (fingerprint: string): void => {
	lsSet(ANALYTICS_IDENTITY_FINGERPRINT, fingerprint);
};
// Null when this browser has never identified, which callers treat as
// "identify is due" rather than an error. Also null in incognito / when
// storage is blocked, which correctly degrades to identifying every load.
export const getAnalyticsIdentityFingerprint = (): string | null => lsGet(ANALYTICS_IDENTITY_FINGERPRINT) || null;
export const removeAnalyticsIdentityFingerprint = (): void => {
	lsRemove(ANALYTICS_IDENTITY_FINGERPRINT);
};

// PLATFORM_HOME_PREFERENCE
export const setPlatformHomePreference = (platformHomePreference: PlatformHomePreference): void => {
	lsSet(PLATFORM_HOME_PREFERENCE, platformHomePreference);
};
export const getPlatformHomePreference = (): PlatformHomePreference => lsGet(PLATFORM_HOME_PREFERENCE) as PlatformHomePreference;
export const removePlatformHomePreference = (): void => {
	lsRemove(PLATFORM_HOME_PREFERENCE);
};

// ACCEPTED_REFERRAL_CODE
export const setAcceptedReferralCode = (referralCode: string): void => {
	lsSet(ACCEPTED_REFERRAL_CODE, referralCode);
};
export const getAcceptedReferralCode = (): string => lsGet(ACCEPTED_REFERRAL_CODE);
export const removeAcceptedReferralCode = (): void => {
	lsRemove(ACCEPTED_REFERRAL_CODE);
};

// ACCEPTED_REFERRAL_DISMISSED
export const setAcceptedReferralDismissed = (): void => {
	lsSet(ACCEPTED_REFERRAL_DISMISSED, trueVal);
};
export const getAcceptedReferralDismissed = (): boolean => lsGet(ACCEPTED_REFERRAL_DISMISSED) === trueVal;
export const removeAcceptedReferralDismissed = (): void => {
	lsRemove(ACCEPTED_REFERRAL_DISMISSED);
};

// HUMAN_CHECK_PASS
export interface StoredHumanCheckPass {
	passId: string | null // null when the server runs the Human Check in off mode
	expiresAtIso: string
}
export const setHumanCheckPass = (pass: StoredHumanCheckPass): void => {
	lsSet(HUMAN_CHECK_PASS, JSON.stringify(pass));
};
export const getHumanCheckPass = (): StoredHumanCheckPass | null => {
	const raw: string = lsGet(HUMAN_CHECK_PASS);
	if (!raw) return null;
	try {
		const parsed: Partial<StoredHumanCheckPass> = JSON.parse(raw);
		if (parsed && typeof parsed.expiresAtIso === 'string') {
			return { passId: typeof parsed.passId === 'string' ? parsed.passId : null, expiresAtIso: parsed.expiresAtIso };
		}
	} catch (e) {
		// Corrupt value; treat as no pass
	}
	return null;
};
export const removeHumanCheckPass = (): void => {
	lsRemove(HUMAN_CHECK_PASS);
};

// CHECKOUT_PROMOTION_CODES
export const setCheckoutPromotionCodes = (checkoutPromotionCodes: Record<ClubKey, string>): void => {
	// Filter checkoutPromotionCodes to only keep keys that exist as a ClubKey
	const clubKeysList: ClubKey[] = _values(CLUB_OFFER_PLAN_INFO).map(({ club }) => club);
	const validPromotionCodes: Partial<Record<ClubKey, string>> = _pick(checkoutPromotionCodes, clubKeysList);

	// Allow for a code that can apply to all plans
	if ((checkoutPromotionCodes as any).ALL) {
		(validPromotionCodes as any).ALL = (checkoutPromotionCodes as any).ALL;
	}

	lsSet(
		CHECKOUT_PROMOTION_CODES,
		JSON.stringify(validPromotionCodes),
	);
};
export const getCheckoutPromotionCodes = (): Record<ClubKey, string> => {
	const stringifiedCheckoutPromotionCodes: string = lsGet(CHECKOUT_PROMOTION_CODES);

	try {
		const checkoutPromotionCodes: Record<ClubKey, string> = JSON.parse(stringifiedCheckoutPromotionCodes);
		if (_isObject(checkoutPromotionCodes)) {
			return checkoutPromotionCodes;
		}
	} catch (e) {
		// continue on to remove the local storage value and return empty value;
	}

	return {} as Record<ClubKey, string>;
};
export const removeCheckoutPromotionCodes = (): void => {
	lsRemove(CHECKOUT_PROMOTION_CODES);
};

/**
 *
 * RUNNITS - LOCAL STORAGE
 *
 */

// RUNNITS_HOME_LIBRARY_SELECTION
export const setRunnitsHomeLibrarySelection = (homeLibrarySelection: string): void => {
	lsSet(RUNNITS_HOME_LIBRARY_SELECTION, homeLibrarySelection);
};
export const getRunnitsHomeLibrarySelection = (): string => lsGet(RUNNITS_HOME_LIBRARY_SELECTION);
export const removeRunnitsHomeLibrarySelection = (): void => {
	lsRemove(RUNNITS_HOME_LIBRARY_SELECTION);
}

// RUNNITS_HOME_BOARDS_SELECTION
export const setRunnitsHomeBoardsSelection = (boardsSelection: string): void => {
	lsSet(RUNNITS_HOME_BOARDS_SELECTION, boardsSelection);
};
export const getRunnitsHomeBoardsSelection = (): string => lsGet(RUNNITS_HOME_BOARDS_SELECTION);
export const removeRunnitsHomeBoardsSelection = (): void => {
	lsRemove(RUNNITS_HOME_BOARDS_SELECTION);
}

// RUNNITS_TOOLS_DIALOG_NAV_TAB
export const setRunnitsToolsDialogNavTab = (navTab: string): void => {
	lsSet(RUNNITS_TOOLS_DIALOG_NAV_TAB, navTab);
};
export const getRunnitsToolsDialogNavTab = (): string => lsGet(RUNNITS_TOOLS_DIALOG_NAV_TAB);
export const removeRunnitsToolsDialogNavTab = (): void => {
	lsRemove(RUNNITS_TOOLS_DIALOG_NAV_TAB);
}

// RUNNITS_SELECTED_IMAGE_MODE
export const setRunnitsSelectedImageModeVal = (selectedImageMode: string): void => {
	lsSet(RUNNITS_SELECTED_IMAGE_MODE, selectedImageMode);
};
export const getRunnitsSelectedImageModeVal = (): string => lsGet(RUNNITS_SELECTED_IMAGE_MODE);
export const removeRunnitsSelectedImageModeVal = (): void => {
	lsRemove(RUNNITS_SELECTED_IMAGE_MODE);
};

// RUNNITS_SELECTED_COLUMNS
export const setRunnitsSelectedColumnsVal = (selectedColumns: number): void => {
	lsSet(RUNNITS_SELECTED_COLUMNS, selectedColumns.toString());
};
export const getRunnitsSelectedColumnsVal = (): number => Number(lsGet(RUNNITS_SELECTED_COLUMNS));
export const removeRunnitsSelectedColumnsVal = (): void => {
	lsRemove(RUNNITS_SELECTED_COLUMNS);
};

export const setRunnitsOwnerSelection = (option: RunnitsOwnerSelection): void => {
	lsSet(RUNNITS_OWNER_SELECTION, option);
};
export const getRunnitsOwnerSelection = (): RunnitsOwnerSelection => lsGet(RUNNITS_OWNER_SELECTION) as RunnitsOwnerSelection;
export const removeRunnitsOwnerSelection = (): void => {
	lsRemove(RUNNITS_OWNER_SELECTION);
};

// RUNNITS_SELECTED_PROMPT_VIEW
export const setRunnitsSelectedPromptView = (option: string): void => {
	lsSet(RUNNITS_SELECTED_PROMPT_VIEW, option);
};
export const getRunnitsSelectedPromptView = (): string => lsGet(RUNNITS_SELECTED_PROMPT_VIEW);
export const removeRunnitsSelectedPromptView = (): void => {
	lsRemove(RUNNITS_SELECTED_PROMPT_VIEW);
};

export const setRunnitsTeamAccessLevel = (option: RunnitsAccessLevel): void => {
	lsSet(RUNNITS_TEAM_ACCESS_LEVEL, option);
};
export const getRunnitsTeamAccessLevel = (): RunnitsAccessLevel => lsGet(RUNNITS_TEAM_ACCESS_LEVEL) as RunnitsAccessLevel;
export const removeRunnitsTeamAccessLevel = (): void => {
	lsRemove(RUNNITS_TEAM_ACCESS_LEVEL);
};

/**
 *
 * RUNNITS BOARD MANAGEMENT - LOCAL STORAGE
 *
 */

// RUNNITS_BOARD_VIEW_MODE
export const setRunnitsBoardViewMode = (viewMode: BoardViewMode): void => {
	lsSet(RUNNITS_BOARD_VIEW_MODE, viewMode);
};
export const getRunnitsBoardViewMode = (): BoardViewMode => lsGet(RUNNITS_BOARD_VIEW_MODE) as BoardViewMode;
export const removeRunnitsBoardViewMode = (): void => {
	lsRemove(RUNNITS_BOARD_VIEW_MODE);
};

// RUNNITS_BOARD_SEARCH_QUERY
export const setRunnitsBoardSearchQuery = (searchQuery: string): void => {
	lsSet(RUNNITS_BOARD_SEARCH_QUERY, searchQuery);
};
export const getRunnitsBoardSearchQuery = (): string => lsGet(RUNNITS_BOARD_SEARCH_QUERY);
export const removeRunnitsBoardSearchQuery = (): void => {
	lsRemove(RUNNITS_BOARD_SEARCH_QUERY);
};

// RUNNITS_BOARD_SORT_OPTION
export const setRunnitsBoardSortOption = (sortOption: BoardSortOption): void => {
	lsSet(RUNNITS_BOARD_SORT_OPTION, sortOption);
};
export const getRunnitsBoardSortOption = (): BoardSortOption => lsGet(RUNNITS_BOARD_SORT_OPTION) as BoardSortOption;
export const removeRunnitsBoardSortOption = (): void => {
	lsRemove(RUNNITS_BOARD_SORT_OPTION);
};

// RUNNITS_BOARD_PROJECT_FILTER
export const setRunnitsBoardProjectFilter = (projectFilter: string): void => {
	lsSet(RUNNITS_BOARD_PROJECT_FILTER, projectFilter);
};
export const getRunnitsBoardProjectFilter = (): string => lsGet(RUNNITS_BOARD_PROJECT_FILTER);
export const removeRunnitsBoardProjectFilter = (): void => {
	lsRemove(RUNNITS_BOARD_PROJECT_FILTER);
};

// RUNNIT_BOARD_TEMPLATES_FILTER
export const setRunnitBoardTemplatesFilter = (filter: BoardsFilter): void => {
	lsSet(RUNNIT_BOARD_TEMPLATES_FILTER, filter);
};
export const getRunnitBoardTemplatesFilter = (): BoardsFilter => {
	const value: BoardsFilter = lsGet(RUNNIT_BOARD_TEMPLATES_FILTER) as BoardsFilter;
	if (value && Object.values(BoardsFilter).includes(value)) {
		return value as BoardsFilter;
	}
	return BoardsFilter.ALL;
};
export const removeRunnitBoardTemplatesFilter = (): void => {
	lsRemove(RUNNIT_BOARD_TEMPLATES_FILTER);
};

// TRAINING_ONBOARDING_DISMISSED
export const setTrainingOnboardingDismissed = (): void => {
	lsSet(RUNNITS_TRAINING_ONBOARDING_DISMISSED, trueVal);
};
export const getTrainingOnboardingDismissed = (): boolean => lsGet(RUNNITS_TRAINING_ONBOARDING_DISMISSED) === trueVal;
export const removeTrainingOnboardingDismissed = (): void => {
	lsRemove(RUNNITS_TRAINING_ONBOARDING_DISMISSED);
};

/**
 *
 * KIT PREFERENCES - LOCAL STORAGE
 *
 */

// Kit preference functions
export const setKitPreference = (kitType: string, kitId: string): void => {
	const key = `RUNNIT_KIT_${kitType}`;
	lsSet(key, kitId);
};

export const getKitPreference = (kitType: string): string | null => {
	const key = `RUNNIT_KIT_${kitType}`;
	return lsGet(key);
};

export const removeKitPreference = (kitType: string): void => {
	const key = `RUNNIT_KIT_${kitType}`;
	lsRemove(key);
};

// Remembers which kit the user last drilled into inside the unified tools
// library dialog (RunnitToolsLibraryDialog). Lets the dialog skip the kit
// picker on subsequent opens, even across page navigations (which is how
// single-tool's "change tool" flow re-mounts everything).
const RUNNIT_LAST_KIT_SELECTION = 'RUNNIT_LAST_KIT_SELECTION';
export const setLastKitSelection = (kitType: string): void => {
	lsSet(RUNNIT_LAST_KIT_SELECTION, kitType);
};
export const getLastKitSelection = (): string | null => {
	return lsGet(RUNNIT_LAST_KIT_SELECTION);
};
export const removeLastKitSelection = (): void => {
	lsRemove(RUNNIT_LAST_KIT_SELECTION);
};

/**
 *
 * RUNNITS SIDE NAV EXPANSION STATE - LOCAL STORAGE
 *
 */

// RUNNITS_SIDE_NAV_GENERATE_EXPANDED
export const setRunnitsSideNavGenerateExpanded = (expanded: boolean): void => {
	lsSet(RUNNITS_SIDE_NAV_GENERATE_EXPANDED, expanded ? trueVal : '0');
};
export const getRunnitsSideNavGenerateExpanded = (): boolean => lsGet(RUNNITS_SIDE_NAV_GENERATE_EXPANDED) === trueVal;
export const removeRunnitsSideNavGenerateExpanded = (): void => {
	lsRemove(RUNNITS_SIDE_NAV_GENERATE_EXPANDED);
};

// RUNNITS_SIDE_NAV_RESOURCES_EXPANDED
export const setRunnitsSideNavResourcesExpanded = (expanded: boolean): void => {
	lsSet(RUNNITS_SIDE_NAV_RESOURCES_EXPANDED, expanded ? trueVal : '0');
};
export const getRunnitsSideNavResourcesExpanded = (): boolean => lsGet(RUNNITS_SIDE_NAV_RESOURCES_EXPANDED) === trueVal;
export const removeRunnitsSideNavResourcesExpanded = (): void => {
	lsRemove(RUNNITS_SIDE_NAV_RESOURCES_EXPANDED);
};

// RUNNITS LIBRARY FILTER SELECTION
export const setRunnitsLibraryFilterSelection = (filterLocation: RunnitLibraryFilterLocation, filterSelection: string): void => {
	lsSet(`${filterLocation}-FILTER`, filterSelection);
};
export const getRunnitsLibraryFilterSelection = (filterLocation: RunnitLibraryFilterLocation): string => {
	const filterSelection = lsGet(`${filterLocation}-FILTER`);
	return filterSelection;
};
export const removeRunnitsLibraryFilterSelection = (filterLocation: RunnitLibraryFilterLocation): void => {
	lsRemove(`${filterLocation}-FILTER`);
};


// ----- Global Search recent searches (per user + account; MRU, cap 8) -----
// Privacy: cleared on sign-out by clearAllGlobalSearchRecents(), called from
// App.vue's authUser watcher. Never synced.
const GLOBAL_SEARCH_RECENTS = 'RD_GLOBAL_SEARCH_RECENTS';
const GLOBAL_SEARCH_RECENTS_CAP = 8;

const globalSearchRecentsKey = (userId: string, accountId: string): string =>
	`${GLOBAL_SEARCH_RECENTS}:${userId}:${accountId}`;

export const getGlobalSearchRecents = (userId: string, accountId: string): string[] => {
	try {
		const raw = lsGet(globalSearchRecentsKey(userId, accountId));
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
	} catch (e) {
		return [];
	}
};

export const addGlobalSearchRecent = (userId: string, accountId: string, query: string): string[] => {
	const normalized: string = (query || '').trim();
	if (!normalized) return getGlobalSearchRecents(userId, accountId);
	const existing: string[] = getGlobalSearchRecents(userId, accountId)
		.filter((q) => q.toLowerCase() !== normalized.toLowerCase());
	const next: string[] = [normalized, ...existing].slice(0, GLOBAL_SEARCH_RECENTS_CAP);
	lsSet(globalSearchRecentsKey(userId, accountId), JSON.stringify(next));
	return next;
};

export const removeGlobalSearchRecents = (userId: string, accountId: string): void => {
	lsRemove(globalSearchRecentsKey(userId, accountId));
};

/**
 * Drop EVERY stored recents list, whoever it belongs to.
 *
 * Sweeps by prefix rather than taking a user and account, because at sign-out
 * neither is reliably in hand: the key is per user AND per account, and one
 * session can leave entries under a personal account plus any number of teams.
 * Asking the caller to enumerate them is how a list gets missed, and a missed
 * list is the previous user's raw search queries readable by whoever signs in
 * next on a shared browser.
 *
 * Iterates a snapshot of the key names, since removing during iteration
 * reindexes localStorage and would skip entries.
 */
export const clearAllGlobalSearchRecents = (): void => {
	if (!window || !window.localStorage) return;
	try {
		const prefix: string = `${GLOBAL_SEARCH_RECENTS}:`;
		const keys: string[] = [];
		for (let i = 0; i < window.localStorage.length; i++) {
			const key: string | null = window.localStorage.key(i);
			if (key && key.startsWith(prefix)) keys.push(key);
		}
		keys.forEach(lsRemove);
	} catch (e) {
		// Incognito / hardened settings throw on access, same as lsGet/lsSet.
		console.error(e);
	}
};
